import React, { useState, useEffect } from "react";
import "./ModalEdicion.css";
import { getMedicoAuth } from "../../../../services/usuarios";
import { updateHistoriaClinica } from "../../../../services/historiaClinica";
import { mapFrontendToBackend, mapBackendToFrontend } from "../../../../services/variableMapper";

const ModalEditarHistoria = ({ historiaClinica, captureModes = {}, onClose, onSave }) => {
  const [medicoActivo, setMedicoActivo] = useState(null);
  
  // Para depuración - ver qué datos recibimos
  useEffect(() => {

  }, [historiaClinica]);

  useEffect(() => {
    const fetchMedico = async () => {
      const medicos = await getMedicoAuth();
      if (medicos.length > 0) {
        setMedicoActivo(medicos[0]);
      }
    };
    fetchMedico();
  }, []);

  // Inicializar estado con estructura anidada - CLAVE: Primero convertimos datos al formato del frontend
  const [datosHistoriaClinica, setDatosHistoriaClinica] = useState(() => {
    // Empezamos con una estructura básica anidada
    const datosIniciales = {
      id: historiaClinica.id,
      padecimientoActual: historiaClinica.padecimientoActual || "",
      notasModificacion: historiaClinica.notasModificacion || {},
      antecedentes: {
        heredoFamiliares: {
          madre: "",
          padre: "",
          hermanos: "",
          abuelos: "",
          otros: ""
        },
        personalesNoPatologicos: {
          Origen: "",
          ocupacion: "",
          escolaridad: "",
          religion: "",
          mascotas: "",
          viajes: "",
          alimentacion: "",
          vivienda: "",
          otros: ""
        },
        personalesPatologicos: {
          ECD: "",
          quirurgicos: "",
          traumaticos: "",
          hospitalizaciones: "",
          infectocontagiosos: "",
          alergias: "",
          transfusionales: "",
          exantematicos: "",
          alcohol: "",
          tabaco: "",
          toxicomanias: "",
          vacunacion: "",
          ginecoObstetricosAndrologicos: "",
          hemotipo: "",
          otros: ""
        }
      },
     sistema_nervioso: historiaClinica.sistema_nervioso || "",
    sistema_cardiovascular: historiaClinica.sistema_cardiovascular || "",
    sistema_respiratorio: historiaClinica.sistema_respiratorio || "",
    sistema_digestivo: historiaClinica.sistema_digestivo || "",
    sistema_genitourinario: historiaClinica.sistema_genitourinario || "",
    sistema_musculoesqueletico: historiaClinica.sistema_musculoesqueletico || "",
    sistema_endocrino: historiaClinica.sistema_endocrino || "",
    sistema_hematologico: historiaClinica.sistema_hematologico || "",
    piel_anexos: historiaClinica.piel_anexos || "",
    
    heredofamiliares_consolidado: historiaClinica.heredofamiliares_consolidado || "",
    no_patologicos_consolidado: historiaClinica.no_patologicos_consolidado || "",
    patologicos_consolidado: historiaClinica.patologicos_consolidado || "",
    interrogatorio_consolidado: historiaClinica.interrogatorio_consolidado || "",

    };

    // Para cada clave en formato "antecedentes.heredoFamiliares.madre", extraemos el valor
    Object.keys(historiaClinica).forEach(key => {
      // Si es un campo anidado (contiene puntos)
      if (key.includes('.')) {
        const partes = key.split('.');
        
        // Solo procesamos campos con estructura conocida
        if (partes.length === 3) {
          // Por ejemplo: antecedentes.heredoFamiliares.madre
          if (!datosIniciales[partes[0]]) datosIniciales[partes[0]] = {};
          if (!datosIniciales[partes[0]][partes[1]]) datosIniciales[partes[0]][partes[1]] = {};
          
          datosIniciales[partes[0]][partes[1]][partes[2]] = historiaClinica[key] || "";
        }
      }
    });

    return datosIniciales;
  });

  // Actualizar estado cuando cambian los datos de la historia clínica
  useEffect(() => {
    setDatosHistoriaClinica(prevState => ({
      ...prevState,
      id: historiaClinica.id,
      padecimientoActual: historiaClinica.padecimientoActual || prevState.padecimientoActual,
      notasModificacion: historiaClinica.notasModificacion || prevState.notasModificacion
    }));
    
    // Actualizar campos anidados
    Object.keys(historiaClinica).forEach(key => {
      if (key.includes('.')) {
        const partes = key.split('.');
        if (partes.length === 3) {
          setDatosHistoriaClinica(prevState => {
            const newState = { ...prevState };
            if (!newState[partes[0]]) newState[partes[0]] = {};
            if (!newState[partes[0]][partes[1]]) newState[partes[0]][partes[1]] = {};
            
            newState[partes[0]][partes[1]][partes[2]] = historiaClinica[key] || "";
            return newState;
          });
        }
      }
    });
  }, [historiaClinica]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Para campos anidados como "antecedentes.heredoFamiliares.madre"
    if (name.includes('.')) {
      const partes = name.split('.');
      
      setDatosHistoriaClinica(prev => {
        const newState = { ...prev };
        
        // Crear estructura anidada si no existe
        if (!newState[partes[0]]) newState[partes[0]] = {};
        if (!newState[partes[0]][partes[1]]) newState[partes[0]][partes[1]] = {};
        
        // Asignar el valor
        newState[partes[0]][partes[1]][partes[2]] = value;
        
        // Gestionar notas de modificación si es necesario
        if (medicoActivo) {
          const nombreMedico = `${medicoActivo.nombre} ${medicoActivo.apellido_paterno}`;
          const fechaModificacion = new Date().toLocaleString();
          
          // Obtener valor inicial (si existe en notas previas o en datos originales)
          let valorOriginal = prev.notasModificacion?.[name]?.primerValor;
          
          if (valorOriginal === undefined) {
            // Buscar en la estructura anidada original
            try {
              valorOriginal = historiaClinica[name] || "";
            } catch (e) {
              valorOriginal = "";
            }
          }
          
          // Actualizar notas de modificación
          newState.notasModificacion = {
            ...newState.notasModificacion,
            [name]: {
              primerValor: valorOriginal,
              ultimaModificacion: {
                valor: value,
                fecha: fechaModificacion,
                medico: nombreMedico
              }
            }
          };
        }
        
        return newState;
      });
    } else {
      // Para campos simples como "padecimientoActual"
      setDatosHistoriaClinica(prev => {
        const newState = { ...prev, [name]: value };
        
        // Gestionar notas de modificación
        if (medicoActivo) {
          const nombreMedico = `${medicoActivo.nombre} ${medicoActivo.apellido_paterno}`;
          const fechaModificacion = new Date().toLocaleString();
          
          let valorOriginal = prev.notasModificacion?.[name]?.primerValor;
          
          if (valorOriginal === undefined) {
            valorOriginal = historiaClinica[name] || "";
          }
          
          newState.notasModificacion = {
            ...newState.notasModificacion,
            [name]: {
              primerValor: valorOriginal,
              ultimaModificacion: {
                valor: value,
                fecha: fechaModificacion,
                medico: nombreMedico
              }
            }
          };
        }
        
        return newState;
      });
    }
  };

// Modifica la función handleSubmit en ModalEditarHistoria.js
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Imprime la estructura completa para depuración
   
      // Intenta obtener el ID del paciente de diferentes maneras posibles
      let pacienteId = null;
      
      if (historiaClinica.paciente?.id) {
        pacienteId = historiaClinica.paciente.id;
      } else if (historiaClinica.paciente_id) {
        pacienteId = historiaClinica.paciente_id;
      } else if (historiaClinica.id_paciente) {
        pacienteId = historiaClinica.id_paciente;
      }
      
      // Si aún no tenemos ID, buscar en el objeto si hay alguna propiedad que contenga la palabra "paciente"
      if (!pacienteId) {
        for (const key in historiaClinica) {
          if (key.includes('paciente') && typeof historiaClinica[key] !== 'object') {
          
            pacienteId = historiaClinica[key];
            break;
          }
        }
      }

      // Si aún no se encontró, verificar si el ID viene dentro de datosHistoriaClinica
      if (!pacienteId && datosHistoriaClinica.paciente_id) {
        pacienteId = datosHistoriaClinica.paciente_id;
      }
      
      if (!pacienteId) {
        console.error("❌ Error: No se encontró el ID del paciente en ninguna propiedad");
        alert("Error: No se pudo determinar el ID del paciente. Por favor, revisa la consola para más detalles.");
        return;
      }
      
    
      // Mapear los datos al formato esperado por el backend
      const datosParaEnviar = mapFrontendToBackend(datosHistoriaClinica, pacienteId);
      
      
      // Eliminar el campo 'laboratorios' para evitar conflictos
      if (datosParaEnviar.laboratorios) {
        delete datosParaEnviar.laboratorios;
      }
      
    
      
      // Enviar la actualización
      const response = await updateHistoriaClinica(
        datosHistoriaClinica.id,
        datosParaEnviar
      );
      
      if (response) {
    
        onSave(response);
        onClose();
      } else {
        console.error("❌ Error al actualizar historia clínica");
        alert("Error al actualizar historia clínica. Consulta la consola para más detalles.");
      }
    } catch (error) {
      console.error("❌ Error en handleSubmit:", error);
      alert("Error al actualizar: " + (error.message || "Revise la consola para más detalles"));
    }
  };

  const renderNota = (campo) => {
    const nota = datosHistoriaClinica.notasModificacion?.[campo];
    if (!nota) return null;
    
    return (
      <p className="nota">
        Valor inicial: '{nota.primerValor}' | Última modificación: '{nota.ultimaModificacion.valor}' 
        por {nota.ultimaModificacion.medico} el {nota.ultimaModificacion.fecha}
      </p>
    );
  };

  const labelsPatologicos = {
  ECD: "Enfermedades crónico-degenerativas",
  quirurgicos: "Quirúrgicos",
  traumaticos: "Traumáticos",
  hospitalizaciones: "Hospitalizaciones",
  infectocontagiosos: "Infectocontagiosos",
  alergias: "Alergias",
  transfusionales: "Transfusionales",
  exantematicos: "Exantemáticos",
  alcohol: "Alcohol",
  tabaco: "Tabaco",
  toxicomanias: "Toxicomanías",
  vacunacion: "Vacunación",
  ginecoObstetricosAndrologicos: "Gineco-obstétricos / Andrológicos",
  hemotipo: "Hemotipo",
  otros: "Otros",
  };
  const labelsNoPatologicos = {
  Origen: "Origen",
  ocupacion: "Ocupación",
  escolaridad: "Escolaridad",
  religion: "Religión",
  mascotas: "Mascotas",
  viajes: "Viajes",
  alimentacion: "Alimentación",
  vivienda: "Vivienda",
  otros: "Otros",
};

const labelsInterrogatorio = {
  sistema_nervioso: "Sistema nervioso",
  sistema_cardiovascular: "Sistema cardiovascular",
  sistema_respiratorio: "Sistema respiratorio",
  sistema_digestivo: "Sistema digestivo",
  sistema_genitourinario: "Sistema genitourinario",
  sistema_musculoesqueletico: "Sistema musculoesquelético",
  sistema_endocrino: "Sistema endocrino",
  sistema_hematologico: "Sistema hematológico",
  piel_anexos: "Piel y anexos",
};

// Decidir modo por rubro: consolidado con datos > individuales con datos > config doctor
  const tieneTexto = (v) => v != null && String(v).trim() !== "";

  const modoHeredo = tieneTexto(datosHistoriaClinica.heredofamiliares_consolidado) ? "breve"
    : Object.values(datosHistoriaClinica.antecedentes?.heredoFamiliares || {}).some(tieneTexto) ? "detallado"
    : (captureModes.heredofamiliares || "detallado");

  const modoNoPat = tieneTexto(datosHistoriaClinica.no_patologicos_consolidado) ? "breve"
    : Object.values(datosHistoriaClinica.antecedentes?.personalesNoPatologicos || {}).some(tieneTexto) ? "detallado"
    : (captureModes.no_patologicos || "detallado");

  const modoPat = tieneTexto(datosHistoriaClinica.patologicos_consolidado) ? "breve"
    : Object.values(datosHistoriaClinica.antecedentes?.personalesPatologicos || {}).filter((v, i) => {
        // Excluir ECD, alergias, hemotipo (campos fijos)
        const keys = Object.keys(datosHistoriaClinica.antecedentes?.personalesPatologicos || {});
        return !["ECD", "alergias", "hemotipo"].includes(keys[i]);
      }).some(tieneTexto) ? "detallado"
    : (captureModes.patologicos || "detallado");

  const modoInterrog = tieneTexto(datosHistoriaClinica.interrogatorio_consolidado) ? "breve"
    : Object.keys(labelsInterrogatorio).some(k => tieneTexto(datosHistoriaClinica[k])) ? "detallado"
    : (captureModes.interrogatorio || "detallado");


  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Editar Historia Clínica</h3>
        <form onSubmit={handleSubmit}>
            <label>Padecimiento Actual:</label>
            <input
                type="text"
                name="padecimientoActual"
                value={datosHistoriaClinica.padecimientoActual || ""}
                onChange={handleChange}
            />
            {renderNota("padecimientoActual")}
          
         {/* 🔹 Antecedentes Heredofamiliares */}
            <h4>Antecedentes Heredofamiliares</h4>
            {modoHeredo === "breve" ? (
              <div>
                <textarea
                  name="heredofamiliares_consolidado"
                  value={datosHistoriaClinica.heredofamiliares_consolidado || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Antecedentes heredofamiliares"
                />
              </div>
            ) : (
              ["madre", "padre", "hermanos", "abuelos", "otros"].map((campo) => (
                <div key={campo}>
                  <label>{campo.charAt(0).toUpperCase() + campo.slice(1)}:</label>
                  <input
                    type="text"
                    name={`antecedentes.heredoFamiliares.${campo}`}
                    value={datosHistoriaClinica.antecedentes?.heredoFamiliares?.[campo] || ""}
                    onChange={handleChange}
                  />
                  {renderNota(`antecedentes.heredoFamiliares.${campo}`)}
                </div>
              ))
            )}

            {/* 🔹 Antecedentes Personales No Patológicos */}
            <h4>Antecedentes Personales No Patológicos</h4>
            {modoNoPat === "breve" ? (
              <div>
                <textarea
                  name="no_patologicos_consolidado"
                  value={datosHistoriaClinica.no_patologicos_consolidado || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Antecedentes personales no patológicos"
                />
              </div>
            ) : (
              ["Origen", "ocupacion", "escolaridad", "religion", "mascotas", "viajes", "alimentacion", "vivienda", "otros"].map((campo) => (
                <div key={campo}>
                  <label>{labelsNoPatologicos[campo]}:</label>
                  <input
                    type="text"
                    name={`antecedentes.personalesNoPatologicos.${campo}`}
                    value={datosHistoriaClinica.antecedentes?.personalesNoPatologicos?.[campo] || ""}
                    onChange={handleChange}
                  />
                  {renderNota(`antecedentes.personalesNoPatologicos.${campo}`)}
                </div>
              ))
            )}

            {/* 🔹 Antecedentes Personales Patológicos */}
            <h4>Antecedentes Personales Patológicos</h4>
            {/* Campos fijos siempre visibles */}
            {["ECD", "alergias", "hemotipo"].map((campo) => (
              <div key={campo}>
                <label>{labelsPatologicos[campo]}:</label>
                <input
                  type="text"
                  name={`antecedentes.personalesPatologicos.${campo}`}
                  value={datosHistoriaClinica.antecedentes?.personalesPatologicos?.[campo] || ""}
                  onChange={handleChange}
                />
                {renderNota(`antecedentes.personalesPatologicos.${campo}`)}
              </div>
            ))}
            {modoPat === "breve" ? (
              <div>
                <textarea
                  name="patologicos_consolidado"
                  value={datosHistoriaClinica.patologicos_consolidado || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Antecedentes personales patológicos"
                />
              </div>
            ) : (
              ["quirurgicos", "traumaticos", "hospitalizaciones", "infectocontagiosos", "transfusionales", "exantematicos", "alcohol", "tabaco", "toxicomanias", "vacunacion", "ginecoObstetricosAndrologicos", "otros"].map((campo) => (
                <div key={campo}>
                  <label>{labelsPatologicos[campo]}:</label>
                  <input
                    type="text"
                    name={`antecedentes.personalesPatologicos.${campo}`}
                    value={datosHistoriaClinica.antecedentes?.personalesPatologicos?.[campo] || ""}
                    onChange={handleChange}
                  />
                  {renderNota(`antecedentes.personalesPatologicos.${campo}`)}
                </div>
              ))
            )}

            {/* 🔹 Interrogatorio por aparatos y sistemas */}
            <h4>Interrogatorio por aparatos y sistemas</h4>
            {modoInterrog === "breve" ? (
              <div>
                <textarea
                  name="interrogatorio_consolidado"
                  value={datosHistoriaClinica.interrogatorio_consolidado || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Interrogatorio por aparatos y sistemas"
                />
              </div>
            ) : (
              ["sistema_nervioso","sistema_cardiovascular","sistema_respiratorio","sistema_digestivo","sistema_genitourinario","sistema_musculoesqueletico",
                "sistema_endocrino","sistema_hematologico","piel_anexos",
              ].map((campo) => (
                <div key={campo}>
                  <label>{labelsInterrogatorio[campo]}:</label>
                  <input
                    type="text"
                    name={campo}
                    value={typeof datosHistoriaClinica[campo] === "string" ? datosHistoriaClinica[campo] : ""}
                    onChange={handleChange}
                  />
                  {renderNota(campo)}
                </div>
              ))
            )}

            <div className="modal-buttons">
              <button type="submit">Guardar Cambios</button>
              <button type="button" onClick={onClose}>
                Cancelar
              </button>
            </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditarHistoria;